import { useRef, useState } from "react";
import { X } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import {
  describeColumnFilter,
  draftToFilter,
  filterToDraft,
  type ColKind,
  type ColumnFilter,
  type FilterDraft,
} from "@/components/clicks/clicksGridEngine";
import { ColumnFilterForm } from "./ColumnFilterForm";

/**
 * A stored condition that does not apply now - its column is hidden, or it
 * belongs to another grouping (parkedColumnFilters). Shown muted, so nothing
 * filters out of sight and nothing comes back as a surprise (owner,
 * 30.10.2026); × removes it from the link.
 */
export function InactiveConditionChip({ label, filter, why, onRemove }: {
  label: string;
  filter: ColumnFilter;
  why: string;
  onRemove: () => void;
}) {
  const text = describeColumnFilter(filter);
  return (
    <span
      title={`Inactive: ${why}`}
      className="inline-flex max-w-[300px] items-center overflow-hidden rounded-full border border-dashed border-border bg-muted/40 text-xs text-muted-foreground"
    >
      <span className="min-w-0 truncate py-1 pr-1 pl-2.5">
        <span className="font-medium">{label}</span> {text} <span className="italic">· inactive</span>
        <span className="sr-only">: {why}</span>
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove the inactive ${label} condition`}
        className="cursor-pointer self-stretch px-2 hover:text-foreground"
      >
        <X className="size-3" aria-hidden />
      </button>
    </span>
  );
}

/**
 * A column condition ("Revenue > 1 000") among the filter chips (owner,
 * 30.10.2026: together with the chips, no separate group). It narrows the
 * table's rows only - the chart and totals follow the other filters. The body
 * edits it, × removes it.
 */
export function ConditionChip({ label, kind, filter, onChange }: {
  label: string;
  kind: ColKind;
  filter: ColumnFilter;
  onChange: (next: ColumnFilter | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FilterDraft>(() => filterToDraft(filter));
  const anchor = useRef<HTMLSpanElement>(null);
  const outside = useRef(false);
  const text = describeColumnFilter(filter);
  const apply = () => {
    setOpen(false);
    onChange(draftToFilter(kind, draft));
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (o) setDraft(filterToDraft(filter));
        setOpen(o);
      }}
    >
      <PopoverAnchor asChild>
        <span ref={anchor} className="inline-flex max-w-[260px] items-center overflow-hidden rounded-full border border-primary/30 bg-primary/10 text-xs">
          <button
            type="button"
            onClick={() => {
              if (open) return setOpen(false);
              setDraft(filterToDraft(filter));
              outside.current = false;
              setOpen(true);
            }}
            title={`${label} ${text} (table rows)`}
            aria-label={`${label} ${text}. Edit`}
            aria-expanded={open}
            className="min-w-0 cursor-pointer truncate py-1 pr-1 pl-2.5 text-left"
          >
            <span className="font-medium">{label}</span> <span className="text-muted-foreground">{text}</span>
          </button>
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label={`Remove ${label} condition`}
            className="cursor-pointer self-stretch px-2 text-muted-foreground hover:text-foreground"
          >
            <X className="size-3" aria-hidden />
          </button>
        </span>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-64 space-y-2 p-3"
        onInteractOutside={(e) => {
          if (anchor.current?.contains(e.target as Node)) e.preventDefault();
          else outside.current = true;
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (!outside.current) anchor.current?.querySelector("button")?.focus();
        }}
      >
        <p className="text-sm font-medium">{label}</p>
        <ColumnFilterForm kind={kind} draft={draft} onDraft={setDraft} onApply={apply} onClear={() => { setOpen(false); onChange(null); }} autoFocus />
        <p className="text-[11px] text-muted-foreground">Narrows the table's rows; the chart and totals keep the other filters.</p>
      </PopoverContent>
    </Popover>
  );
}
